import {
    api,
    deleteWithOptionalAuthorization,
    getWithOptionalAuthorizationGeneric,
    postWithOptionalAuthorization,
} from "./api";
import { FAQS_BASE } from "./apiConstant";
import { getToken } from "./auth";
import { decryptMessage } from "../utils/decrypt";

// ✅ Matches backend response
export type FaqImageResponse = {
    id: string;
    faqid: string;
    fileName: string;
    fileUrl: string;
    createdAt: string;
};

export type FaqResponse = {
    id: string;
    question: string;
    answer: string;
    createdAt: string;
    faqImagesResponses: FaqImageResponse[];
};

// ✅ For create/update payload
export interface FaqCreatePayload {
    id?: string;
    question: string;
    answer: string;
    faqImagesIDs?: string[];
    status?: number; // optional for draft/publish if backend supports it
}

const FAQsApi = {
    /**
     * Get all FAQs
     */
    getFaqsList: async (): Promise<FaqResponse[]> => {
        try {
            const response = await getWithOptionalAuthorizationGeneric<FaqResponse[]>(`${FAQS_BASE}`, true);
            const token = getToken(); if (!token) throw new Error("No token found");
            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData
        } catch (error) {
            console.error("Error fetching FAQs:", error);
            throw error;
        }
    },

    /**
     * Get FAQ by ID
     */
    getFaqById: async (id: string): Promise<FaqResponse> => {
        try {
            const encryptedResponse = await getWithOptionalAuthorizationGeneric<any>(
                `${FAQS_BASE}/${id}`,
                true
            );

            const token = getToken();
            const decrypted = await decryptMessage(encryptedResponse.data.Data, token);
            return decrypted;
        } catch (error) {
            console.error("Error fetching FAQ:", error);
            throw error;
        }
    },

    /**
     * Create a new FAQ
     */
    createFaq: async (payload: FaqCreatePayload): Promise<FaqResponse> => {
        try {
            const response = await postWithOptionalAuthorization(`${FAQS_BASE}`, payload, true, false);
            const token = getToken();
            if (!token) throw new Error("No token found");
            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error) {
            console.error("Error creating FAQ:", error);
            throw error;
        }
    },

    /**
     * Update an existing FAQ
     */
    updateFaq: async (payload: FaqCreatePayload): Promise<FaqResponse> => {
        try {
            const token = getToken();
            if (!token) throw new Error("No token found");
            debugger;

            const response = await api.put(`${FAQS_BASE}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error) {
            console.error("Error updating FAQ:", error);
            throw error;
        }
    },

    /**
     * Delete FAQ
     */
    deleteFaq: async (id: string): Promise<void> => {
        try {
            await deleteWithOptionalAuthorization(`${FAQS_BASE}/${id}`, true);
        } catch (error) {
            console.error("Error deleting FAQ:", error);
            throw error;
        }
    },

    /**
     * Upload FAQ image
     */
    uploadFaqImage: async (faqId: string, file: File): Promise<FaqImageResponse> => {
        try {
            const formData = new FormData();
            formData.append("FAQID", faqId);
            formData.append("FAQFile", file);

            const response = await postWithOptionalAuthorization(`${FAQS_BASE}/PostFAQImages`, formData, true, true);

            const token = getToken();
            if (!token) throw new Error("No token found");
            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error) {
            console.error("Error uploading FAQ image:", error);
            throw error;
        }
    },

    updateFinalAnswerFaq: async (payload: FaqCreatePayload): Promise<any> => {
        try {
            const token = getToken();
            if (!token) throw new Error("No token found");

            const response = await api.patch(
                `${FAQS_BASE}/Answer`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const decryptedData = await decryptMessage(response.data.Data, token);
            return decryptedData;
        } catch (error: any) {
            console.error("Error updating Answer:", error.response);
            throw error;
        }
    },
};

export default FAQsApi;
